import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { UserDto } from "../../../../../../interfaces/team/RegionHierarchy";
import { ShareFileFormData } from "../../../../../../interfaces/file/DriveFile";
import { getAllUsersWithSameOrganizationandRegion } from "../../../../../../services/userServices/TeamService";
import { toast } from "react-toastify";
import { uploadAndShareFile } from "../../../../../../services/fileServices/FileService";
import {
  ALLOWED_FILE_MIMETYPES,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_KB,
  MAX_FILE_SIZE_MB,
} from "../../../../../../Data/SD";

// Defualt values for the form data
const defaultSharedFileFormData: ShareFileFormData = {
  selectedUserIds: [],
  fileType: "",
  selectedImage: null,
};

interface ShareFileModalProps {
  showSharedFileModal: boolean;
  handleCloseSharedFileModal: () => void;
  authToken: string;
  currentUserId?: string;
  refreshCurrentFolder: () => void;
}

const ShareFileModal = ({
  showSharedFileModal,
  handleCloseSharedFileModal,
  authToken,
  currentUserId,
  refreshCurrentFolder,
}: ShareFileModalProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [userLoading, setUserLoading] = useState<boolean>(false);
  const [allUsers, setAllUsers] = useState<UserDto[]>([]);
  const [formData, setFormData] = useState<ShareFileFormData>(
    defaultSharedFileFormData
  );
  const [formErrors, setFormErrors] = useState<{
    selectedUserIds?: string;
    fileType?: string;
    selectedImage?: string;
  }>({});

  useEffect(() => {
    fetchAllUser();

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle file type change
  const handleFileTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => ({
      ...prev,
      fileType: e.target.value,
    }));

    setFormErrors((prev) => ({ ...prev, fileType: undefined }));
  };

  const fetchAllUser = async () => {
    setUserLoading(true);
    try {
      const response = await getAllUsersWithSameOrganizationandRegion(
        authToken,
        {
          searchTerm: null,
          sortBy: null,
          sortDescending: true,
          pageNumber: 1,
          pageSize: 1000,
        }
      );
      if (response) {
        const usersAccessMe = response.data.filter(
          (u) => u.id != currentUserId
        );
        setAllUsers(usersAccessMe);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setUserLoading(false);
    }
  };

  // Handle image upload (drag and drop or file input)
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; // pick the first file
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.warning(`Please Select Less than ${MAX_FILE_SIZE_MB} MB.`);
      e.target.value = ""; // reset input
      return;
    }

    setFormData((prev) => ({
      ...prev,
      selectedImage: file,
    }));
    setFormErrors((prev) => ({
      ...prev,
      selectedImage: file ? undefined : "Please select an image to share.",
    }));
  };
  const toggleSelection = (id: string) => {
    //   setSelectedIds((prev) =>
    //     prev.includes(id) ? prev.filter((val) => val !== id) : [...prev, id]
    //   );

    setFormData((prev) => {
      const isSelected = prev.selectedUserIds.includes(id);
      return {
        ...prev,
        selectedUserIds: isSelected
          ? prev.selectedUserIds.filter((userId) => userId !== id) // fix here
          : [...prev.selectedUserIds, id],
      };
    });

    setFormErrors((prev) => ({ ...prev, selectedUserIds: undefined }));
  };

  const selectedNames =
    allUsers
      .filter((u) => formData.selectedUserIds.includes(u.id))
      .map((u) => `${u.firstName} ${u.lastName}`)
      .join(", ") || "Select Employee";

  // Handle file sharing
  const handleclickShare = async () => {
    const newErrors: typeof formErrors = {};
    if (formData.selectedUserIds.length === 0) {
      newErrors.selectedUserIds = "Please select at least one user.";
    }
    if (formData.fileType === "") {
      newErrors.fileType = "Please select a file type.";
    }
    if (formData.selectedImage === null) {
      newErrors.selectedImage = "Please select an image to share.";
    }
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fill in all required fields.");
      setFormErrors(newErrors);
      return;
    }

    if (formData.selectedImage == null || !currentUserId) {
      toast.error("Current user not found");
      return;
    }
    try {
      setUserLoading(true);
      const saveResposne = await uploadAndShareFile(
        authToken,
        formData.selectedImage,
        currentUserId,
        formData.selectedUserIds,
        formData.fileType
      );
      if (saveResposne.statusCode == 200) {
        refreshCurrentFolder();
        toast.success(saveResposne.message);
      } else {
        toast.error(saveResposne.message);
        return;
      }
      setFormData(defaultSharedFileFormData);
      handleCloseSharedFileModal();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setUserLoading(false);
    }
  };
  return (
    <>
      <Modal
        show={showSharedFileModal}
        onHide={handleCloseSharedFileModal}
        className="custom_popup"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Shared Files</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="formFile" className="custom_input_flled mb-0">
            {/* Dropdown 1 */}
            <div className="mb-4 relative custom_input_flled" ref={dropdownRef}>
              <label>Choose employee</label>
              <div
                className="form-control"
                onClick={() => setIsOpen((prev) => !prev)}
              >
                {selectedNames}
              </div>

              {isOpen && (
                <div className="custom_check_list">
                  {userLoading ? (
                    <div className="p-2 text-gray-500 dark:text-gray-300">
                      Loading...
                    </div>
                  ) : (
                    allUsers.map((user) => (
                      <div key={user.id} className="custom_check_list_label">
                        <input
                          type="checkbox"
                          checked={formData.selectedUserIds.includes(user.id)}
                          onChange={() => toggleSelection(user.id)}
                        />
                        <label>
                          {user.firstName} {user.lastName}
                        </label>
                      </div>
                    ))
                  )}
                </div>
              )}
              {formErrors.selectedUserIds && (
                <small className="text-red-700">
                  {" "}
                  {formErrors.selectedUserIds}
                </small>
              )}
            </div>

            <Form.Group
              className="custom_input_flled"
              controlId="exampleForm.ControlInput1"
            >
              <Form.Label>File Type</Form.Label>
              <Form.Select
                aria-label="Default select example"
                onChange={handleFileTypeChange}
              >
                <option value="">Select File Type</option>
                <option value="CaseFile">Case File </option>
                <option value="UserFile">User File</option>
              </Form.Select>
              {formErrors.fileType && (
                <small className="text-red-700"> {formErrors.fileType}</small>
              )}
            </Form.Group>

            <Form.Group
              controlId="formFile"
              className="custom_input_flled mb-0"
            >
              <Form.Label>Choose File</Form.Label>
              <Form.Control
                type="file"
                onChange={(e) => {
                  handleImageUpload(e as React.ChangeEvent<HTMLInputElement>);
                }}
                accept={ALLOWED_FILE_MIMETYPES.join(",")}
              />
              {formErrors.selectedImage && (
                <small className="text-red-700">
                  {" "}
                  {formErrors.selectedImage}
                </small>
              )}
              <Form.Text className="text-muted">
                Maximum file size: {MAX_FILE_SIZE_MB} MB
              </Form.Text>
            </Form.Group>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseSharedFileModal}>
            Close
          </Button>
          <Button variant="primary" onClick={handleclickShare}>
            Share File
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ShareFileModal;
